import React, { useState, useEffect, useRef } from "react";
import { Snackbar } from "@mui/material";
import { Typography, Button } from "@mhome/ui";
import { useLocation } from "react-router-dom";
import { useRecoilValue } from "recoil";
import { safeAreaInsetsState } from "../global-state";

/**
 * A generic toast component that can be used for various notifications
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether the toast should be shown
 * @param {Function} props.onClose - Function to call when the toast should be closed
 * @param {Function} props.onAction - Function to call when the action button is clicked
 * @param {string} props.message - The message to display in the toast
 * @param {string} props.actionButtonText - Text for the action button
 * @param {Array<string>} props.excludedPaths - Paths where the toast should not be shown
 * @param {Function} props.checkCondition - Function that returns true if the toast should be shown
 * @param {number} props.delay - Delay in ms before showing the toast
 */
const ImportantToast = ({
  isOpen,
  onClose,
  onAction,
  message,
  actionButtonText = "Reconnect",
  excludedPaths = [],
  checkCondition = () => false,
  delay = 2000,
}) => {
  const [open, setOpen] = useState(false);
  const timerRef = useRef(null);
  const location = useLocation();
  const safeAreaInsets = useRecoilValue(safeAreaInsetsState);

  useEffect(() => {
    // Don't show toast on excluded paths
    if (excludedPaths.includes(location.pathname)) {
      return;
    }

    if (isOpen) {
      // Clear any existing timer first
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }

      // Set a new timer
      timerRef.current = setTimeout(() => {
        if (checkCondition()) {
          // skip
        } else {
          setOpen(true);
        }
      }, delay);
    } else {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      setOpen(false);
    }

    // Cleanup on unmount
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [isOpen, location.pathname, excludedPaths, checkCondition, delay]);

  const handleAction = () => {
    if (onAction) {
      onAction();
    }
  };

  const handleClose = (event, reason) => {
    // Prevent closing when clicking outside
    if (reason === "clickaway") {
      return;
    }

    if (onClose) {
      onClose();
    }
    setOpen(false);
  };

  return (
    <Snackbar
      open={open}
      anchorOrigin={{ vertical: "top", horizontal: "center" }}
      onClose={handleClose}
      autoHideDuration={null}
      style={{
        top: `calc(${safeAreaInsets.top}px + 24px)`,
        zIndex: parseInt(
          getComputedStyle(document.documentElement).getPropertyValue(
            "--z-index-important-toast"
          ) || "1400",
          10
        ),
      }}
      ContentProps={{
        style: {
          minWidth: "auto",
          padding: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        },
      }}
      PaperProps={{
        style: {
          backgroundColor: "transparent",
          boxShadow: "none",
        },
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          paddingLeft: "20px", // px: 2.5 = 20px
          paddingRight: "20px",
          paddingTop: "8px", // py: 1 = 8px
          paddingBottom: "8px",
          backgroundColor: "hsl(var(--card))",
          borderRadius: "12px",
          boxShadow:
            typeof document !== "undefined" &&
            document.documentElement.classList.contains("dark")
              ? "0 8px 32px rgba(0,0,0,0.8)"
              : "0 4px 16px rgba(0,0,0,0.2)",
          border:
            typeof document !== "undefined" &&
            document.documentElement.classList.contains("dark")
              ? "1px solid hsl(var(--border))"
              : "none",
        }}
      >
        <Typography
          variant="body2"
          style={{ fontSize: 14, marginRight: "8px" }}
        >
          {message}
        </Typography>
        <Button
          variant="text"
          color="primary"
          size="small"
          onClick={handleAction}
          className="normal-case"
          style={{
            fontSize: 13,
            minWidth: 0,
            paddingLeft: "8px",
            paddingRight: "8px",
            paddingTop: "4px",
            paddingBottom: "4px",
          }}
        >
          {actionButtonText}
        </Button>
      </div>
    </Snackbar>
  );
};

export default ImportantToast;
